import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Archive, Search, RotateCcw, Trash2,
  Building2, AlertTriangle, CheckCircle2, FileText,
  CheckSquare, XCircle, AlertOctagon
} from 'lucide-react';
import { toast } from 'sonner';
import { MainLayout } from '../components/MainLayout';

export const ArchivedEnquiriesPage = () => {
  const { user, authFetch } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkRestoreModal, setShowBulkRestoreModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Redirect non-directors
  useEffect(() => {
    if (user && user.role !== 'director') {
      navigate('/');
      toast.error('Director access required');
    }
  }, [user, navigate]);

  useEffect(() => {
    loadArchivedEnquiries();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadArchivedEnquiries = async () => {
    try {
      const res = await authFetch('/enquiries/archived/list');
      if (res.ok) {
        const data = await res.json();
        setEnquiries(data.enquiries || []);
        setSelectedIds([]);
      } else if (res.status === 403) {
        navigate('/');
        toast.error('Director access required');
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedEnquiry) return;
    
    setRestoring(true);
    try {
      const res = await authFetch(`/enquiries/${selectedEnquiry.id}/restore`, {
        method: 'PATCH'
      });
      
      if (res.ok) {
        toast.success('Enquiry restored successfully');
        setShowRestoreModal(false);
        setSelectedEnquiry(null);
        loadArchivedEnquiries();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to restore enquiry');
      }
    } catch (err) {
      toast.error('Failed to restore enquiry');
    } finally {
      setRestoring(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!selectedEnquiry) return;
    
    setDeleting(true);
    try {
      const res = await authFetch(`/enquiries/${selectedEnquiry.id}/permanent`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Enquiry permanently deleted');
        setShowPermanentDeleteModal(false);
        setSelectedEnquiry(null);
        loadArchivedEnquiries();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete enquiry');
      }
    } catch (err) {
      toast.error('Failed to delete enquiry');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkRestore = async () => {
    if (selectedIds.length === 0) return;
    
    setBulkProcessing(true);
    try {
      const res = await authFetch('/enquiries/bulk/restore', {
        method: 'POST',
        body: { enquiry_ids: selectedIds }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Restored ${data.restored_count} enquiries`);
        setShowBulkRestoreModal(false);
        loadArchivedEnquiries();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to restore enquiries');
      }
    } catch (err) {
      toast.error('Failed to restore enquiries');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkPermanentDelete = async () => {
    if (selectedIds.length === 0) return;
    
    setBulkProcessing(true);
    try {
      const res = await authFetch('/enquiries/bulk/permanent-delete', {
        method: 'POST',
        body: { enquiry_ids: selectedIds }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Permanently deleted ${data.deleted_count} enquiries`);
        setShowBulkDeleteModal(false);
        loadArchivedEnquiries();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete enquiries');
      }
    } catch (err) {
      toast.error('Failed to delete enquiries');
    } finally {
      setBulkProcessing(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredEnquiries.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredEnquiries.map(e => e.id));
    }
  };

  const openRestoreModal = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowRestoreModal(true);
  };

  const openPermanentDeleteModal = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowPermanentDeleteModal(true);
  };

  const filteredEnquiries = enquiries.filter(e => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      e.project_name?.toLowerCase().includes(query) ||
      e.client_name?.toLowerCase().includes(query) ||
      e.reference_number?.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (user?.role !== 'director') {
    return null;
  }

  return (
    <MainLayout>
      <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="archived-enquiries-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-slate-600 to-slate-700 rounded-2xl flex items-center justify-center shadow-lg">
                <Archive className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1e3a5f]">
                  Archived Enquiries
                </h1>
                <p className="text-slate-500">
                  {enquiries.length} archived enquir{enquiries.length === 1 ? 'y' : 'ies'} • Director access only
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/audit-logs')}
              className="rounded-full"
              data-testid="view-audit-logs-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Audit Logs
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/enquiries')}
              className="rounded-full"
            >
              <FileText className="w-4 h-4 mr-2" />
              Back to Enquiries
            </Button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.length > 0 && (
          <div className="bg-[#1e3a5f] rounded-2xl p-4 flex items-center justify-between shadow-lg animate-fadeIn">
            <div className="flex items-center gap-3 text-white">
              <CheckSquare className="w-5 h-5" />
              <span className="font-medium">{selectedIds.length} selected</span>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setSelectedIds([])}
                className="rounded-full bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                Clear
              </Button>
              <Button
                onClick={() => setShowBulkRestoreModal(true)}
                className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="bulk-restore-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Restore Selected
              </Button>
              <Button
                onClick={() => setShowBulkDeleteModal(true)}
                className="rounded-full bg-red-500 hover:bg-red-600 text-white"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Permanently
              </Button>
            </div>
          </div>
        )}

        {/* Search & Select All */}
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search archived enquiries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 rounded-full bg-white border-slate-200"
              data-testid="archive-search"
            />
          </div>
          
          {filteredEnquiries.length > 0 && (
            <Button
              variant="outline"
              onClick={toggleSelectAll}
              className="rounded-full"
              data-testid="select-all-btn"
            >
              {selectedIds.length === filteredEnquiries.length ? (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Deselect All
                </>
              ) : (
                <>
                  <CheckSquare className="w-4 h-4 mr-2" />
                  Select All ({filteredEnquiries.length})
                </>
              )}
            </Button>
          )}
        </div>

        {/* Enquiries List */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-pulse text-slate-500">Loading archived enquiries...</div>
            </div>
          ) : filteredEnquiries.length > 0 ? (
            filteredEnquiries.map(enquiry => (
              <div
                key={enquiry.id}
                className={`bg-white rounded-2xl border shadow-sm overflow-hidden hover:shadow-md transition-all ${
                  selectedIds.includes(enquiry.id) 
                    ? 'border-[#1e3a5f] ring-2 ring-[#1e3a5f]/20' 
                    : 'border-slate-200'
                }`}
                data-testid={`archived-${enquiry.id}`}
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <div 
                      className="pt-1 cursor-pointer"
                      onClick={() => toggleSelect(enquiry.id)}
                    >
                      <Checkbox
                        checked={selectedIds.includes(enquiry.id)}
                        onCheckedChange={() => toggleSelect(enquiry.id)}
                        className="w-5 h-5"
                        data-testid={`checkbox-${enquiry.id}`}
                      />
                    </div>
                    
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Trash2 className="w-5 h-5 md:w-6 md:h-6 text-slate-400" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs font-medium rounded-full">
                          Archived
                        </span>
                        <span className="text-xs md:text-sm text-slate-500 font-mono">
                          {enquiry.reference_number}
                        </span>
                      </div>
                      
                      <h3 className="font-heading font-semibold text-[#1e3a5f] text-base md:text-lg truncate">
                        {enquiry.project_name}
                      </h3>
                      
                      <div className="flex items-center gap-2 md:gap-4 mt-2 text-xs md:text-sm text-slate-500 flex-wrap">
                        <div className="flex items-center gap-1">
                          <Building2 className="w-3 h-3 md:w-4 md:h-4" />
                          <span className="truncate max-w-[120px] md:max-w-none">{enquiry.client_name}</span>
                        </div>
                        {enquiry.estimated_value && (
                          <span className="font-medium text-slate-700">
                            £{enquiry.estimated_value.toLocaleString('en-GB')}
                          </span>
                        )}
                      </div>
                      
                      {/* Deletion Info - Simplified on mobile */}
                      <div className="mt-2 md:mt-3 p-2 md:p-3 bg-red-50 rounded-xl border border-red-100">
                        <div className="flex items-start gap-2 text-xs md:text-sm">
                          <AlertTriangle className="w-3 h-3 md:w-4 md:h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-red-700 truncate">
                              <span className="font-medium">Archived</span> {formatDate(enquiry.deleted_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions - Stack on mobile */}
                    <div className="flex flex-row md:flex-col gap-2 w-full md:w-auto mt-2 md:mt-0">
                      <Button
                        onClick={() => openRestoreModal(enquiry)}
                        className="flex-1 md:flex-auto bg-emerald-600 hover:bg-emerald-700 text-white rounded-full text-xs md:text-sm px-3 md:px-4"
                        data-testid={`restore-${enquiry.id}`}
                      >
                        <RotateCcw className="w-4 h-4 md:mr-2" />
                        <span className="md:inline">Restore</span>
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => openPermanentDeleteModal(enquiry)}
                        className="flex-1 md:flex-auto border-red-300 text-red-600 hover:bg-red-50 rounded-full text-xs md:text-sm px-3 md:px-4"
                        data-testid={`permanent-delete-${enquiry.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Forever
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Archive className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-heading font-semibold text-slate-700 mb-2">
                No Archived Enquiries
              </h3>
              <p className="text-slate-500 max-w-sm mx-auto">
                {searchQuery 
                  ? 'No archived enquiries match your search.'
                  : 'There are no archived or deleted enquiries to display.'}
              </p>
            </div>
          )}
        </div>

        {/* Single Restore Modal */}
        <Dialog open={showRestoreModal} onOpenChange={setShowRestoreModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-heading text-[#1e3a5f]">
                <RotateCcw className="w-5 h-5 text-emerald-600" />
                Restore Enquiry
              </DialogTitle>
            </DialogHeader>
            
            {selectedEnquiry && (
              <div className="space-y-4 pt-2">
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                  <p className="text-emerald-800 text-sm">
                    You are about to restore the following enquiry:
                  </p>
                  <div className="mt-3">
                    <p className="font-semibold text-emerald-900">
                      {selectedEnquiry.project_name}
                    </p>
                    <p className="text-sm text-emerald-700">
                      {selectedEnquiry.client_name} • {selectedEnquiry.reference_number}
                    </p>
                  </div>
                </div>
                
                <p className="text-slate-600 text-sm">
                  The enquiry will be restored to <strong>&quot;New&quot;</strong> status and will appear in the main enquiries list.
                </p>
                
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowRestoreModal(false)}
                    className="flex-1 rounded-full"
                    disabled={restoring}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleRestore}
                    className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700"
                    disabled={restoring}
                    data-testid="confirm-restore-btn"
                  >
                    {restoring ? (
                      <span className="animate-pulse">Restoring...</span>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Confirm Restore
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Single Permanent Delete Modal */}
        <AlertDialog open={showPermanentDeleteModal} onOpenChange={setShowPermanentDeleteModal}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <AlertOctagon className="w-5 h-5" />
                Permanently Delete Enquiry?
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                {selectedEnquiry && (
                  <>
                    <p>
                      You are about to <strong className="text-red-600">permanently delete</strong> the following enquiry:
                    </p>
                    <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="font-semibold text-red-900">
                        {selectedEnquiry.project_name}
                      </p>
                      <p className="text-sm text-red-700">
                        {selectedEnquiry.client_name} • {selectedEnquiry.reference_number}
                      </p>
                    </div>
                    <p className="text-red-600 font-medium">
                      ⚠️ This action CANNOT be undone. All related quotes and decisions will also be deleted.
                    </p>
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-full" disabled={deleting}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handlePermanentDelete}
                className="rounded-full bg-red-600 hover:bg-red-700"
                disabled={deleting}
                data-testid="confirm-permanent-delete-btn"
              >
                {deleting ? 'Deleting...' : 'Delete Permanently'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk Restore Modal */}
        <Dialog open={showBulkRestoreModal} onOpenChange={setShowBulkRestoreModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-heading text-[#1e3a5f]">
                <RotateCcw className="w-5 h-5 text-emerald-600" />
                Restore {selectedIds.length} Enquiries
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-2">
              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <p className="text-emerald-800 text-sm">
                  You are about to restore <strong>{selectedIds.length} enquiries</strong> back to the main list.
                </p>
              </div>
              
              <p className="text-slate-600 text-sm">
                All selected enquiries will be restored to <strong>&quot;New&quot;</strong> status.
              </p>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowBulkRestoreModal(false)}
                  className="flex-1 rounded-full"
                  disabled={bulkProcessing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkRestore}
                  className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700"
                  disabled={bulkProcessing}
                  data-testid="confirm-bulk-restore-btn"
                >
                  {bulkProcessing ? 'Restoring...' : `Restore ${selectedIds.length} Enquiries`}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Permanent Delete Modal */}
        <AlertDialog open={showBulkDeleteModal} onOpenChange={setShowBulkDeleteModal}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <AlertOctagon className="w-5 h-5" />
                Permanently Delete {selectedIds.length} Enquiries?
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                <p>
                  You are about to <strong className="text-red-600">permanently delete {selectedIds.length} enquiries</strong>.
                </p>
                <p className="text-red-600 font-medium">
                  ⚠️ This action CANNOT be undone. All related quotes and decisions will also be deleted.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-full" disabled={bulkProcessing}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleBulkPermanentDelete}
                className="rounded-full bg-red-600 hover:bg-red-700"
                disabled={bulkProcessing}
                data-testid="confirm-bulk-delete-btn"
              >
                {bulkProcessing ? 'Deleting...' : `Delete ${selectedIds.length} Permanently`}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
};

export default ArchivedEnquiriesPage;
